import {
    Table, notification, Tooltip, Popconfirm, Switch
} from "antd";
import { ColumnsType } from "antd/es/table";
import {Icon} from "ts-react-feather-icons";
import React, { useEffect, useState } from "react";
import { Card, Col, Row, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getStoryCollections, updateCollection } from "../../helpers/api/storyCollections";
import StoryCollectionDrawer from "../../components/storyCollection/storyCollectionDrawer";
import { ActionType } from "../../constants/actionType";
import { deleteCollectionConfig, fetchCollectionConfigs, updateCollectionConfig } from "../../helpers/api/storyCollectionConfig";
import CollectionConfigDrawer from "../../components/storyCollection/collectionConfigDawer";


export interface StoryCollectionModel {
    key: React.Key;
    id: string;
    name: string;
    description: string;
    configId?: string;
    imageUrl: string;
    status: string;
    createdAt: Date;
    modifiedAt: Date;
    createdBy: string;
    modifiedBy: string;
    config: CollectionConfig
    storyIds: string[];
}

export interface CollectionConfig {
    id: string;
    name: string;
    config?: any;
    status: string;
    createdAt: Date;
    modifiedAt: Date;
}



const StoryCollection = () => {

    const [storyCollections, setStoryCollections] = React.useState<StoryCollectionModel[]>([]);
    const [ collectionConfigs, setCollectionConfigs ] = React.useState<CollectionConfig[]>([]);
    const [storyDrawerVisibility, setStoryDrawerVisibility] = useState<boolean>(false);
    const [ configDrawerVisibility, setConfigDrawerVisibility ] = useState<boolean>(false);
    const [ actionType, setActionType ] = useState<string>("CREATE");
    const [ configActionType, setConfigActionType ] = useState<string>("CREATE");
    const [ storyCollectionDetail, setStoryCollectionDetail ] = useState<StoryCollectionModel | null>(null);
    const [ currentConfig, setCurrentConfig ] = useState<CollectionConfig | null>(null);
    const [api, contextHolder] = notification.useNotification();

    useEffect(() => {
        loadStoryCollections();
        loadCollectionConfigs();
    }, []);

    const loadStoryCollections = async () => {
        const storyCollections = await getStoryCollections();
        setStoryCollections(storyCollections.data);
    }

    const loadCollectionConfigs = async () => {
        const collectionConfigs = await fetchCollectionConfigs();
        setCollectionConfigs(collectionConfigs.data);
    }

    const updateCollectionStatus = async (collection: StoryCollectionModel, status: string) => {
        const newStatus = status === "active" ? "inactive" : "active";
        try {
            const requestBody: any = {};
            requestBody["data"] = JSON.stringify({ status: newStatus });

            await updateCollection(collection.id, requestBody);

            loadStoryCollections();            
            api.success({
                message: `Successfull notification`,
                description: 'Update collection status successfully!',
                placement: 'topRight'
            });
        } catch (error) {
            console.error(error);
            showError(error as string);
        }
    }

    const updateCollectionConfigStatus = async (collectionConfig: CollectionConfig, status: string) => {
        const newStatus = status === "active" ? "inactive" : "active";
        try {
            await updateCollectionConfig(collectionConfig.id, { status: newStatus });

            await loadCollectionConfigs();            
            api.success({
                message: `Successfull notification`,
                description: 'Update collection Config status successfully!',
                placement: 'topRight'
            });
        } catch (error) {
            console.error(error);
            showError(error as string);
        }
    }

    const showError = (errorMessage: string) => {
        api.error({
          message: `Error notification`,
          description: <span className="text-danger">{errorMessage || 'There\'re some errors occured!'}</span>,
          placement: 'topRight'
        });
    }

    const triggerUpdate = async (collectionId: string) => {
        try {
            if (!collectionId || collectionId?.trim()?.length === 0) {
                return;
            }
            const collectionDetail = storyCollections.find((collection) => collection.id === collectionId) || null;
            setStoryCollectionDetail(collectionDetail);
            setActionType(ActionType.UPDATE);
            setStoryDrawerVisibility(true);
        } catch (err) {
            console.error(err);
            showError(err as string);
        }
    }

    const triggerUpdateConfig = async (record: any) => {
        try {
            setCurrentConfig(record);
            setConfigActionType(ActionType.UPDATE);
            setConfigDrawerVisibility(true);
        } catch (err) {
            console.error(err);
            showError(err as string);
        }
    }

    const triggerDeleteConfig = async (record: any) => {
        try {
            await deleteCollectionConfig(record.id);
            api.success({
                message: `Successfull notification`,
                description: 'Delete config successfully!',
                placement: 'topRight'
            });
            await loadCollectionConfigs();
        } catch (err) {
            console.error(err);
            showError(err as string);
        }
    }

    const columns: ColumnsType<StoryCollectionModel> = [
        {
            title: "No.",
            render: (text, record, index) => {
                return (
                    <span>{index + 1}</span>
                )
            },
        },
        {
            title: "Name",
            dataIndex: "name",
            render: (text, record, index) => {
                return (
                    <Link to={`/collections/${record.id}`} className="text-primary">
                        {record.name}
                    </Link>
                )
            },
        },
        {
            title: "Config",
            dataIndex: "name",
            render: (text, record, index) => {
                return (
                    <span>{record?.config ? record.config?.name : ''}</span>
                )
            },
        },
        {
            title: "Published?",
            dataIndex: "activated",
            render: (text, record, index) => {
                return (
                    <Popconfirm
                        title={`${record.status === "active" ? "Hide" : "Show"} collection`}
                        description={`Are you sure to ${record.status === "active" ? "hide" : "show"} this collection?`}
                        onConfirm={() => updateCollectionStatus(record, record.status)}
                        okText="OK"
                        cancelText="Cancel"
                    >
                        <Switch checked={record.status === 'active'} />
                    </Popconfirm>
                )
            },
        },
        {
            title: "Created by",
            dataIndex: "createdBy",
        },
        {
            title: "Created at",
            dataIndex: "createdAt",
            render: (text, record, index) => {
                return <span>{record?.createdAt ? new Date(record?.createdAt).toLocaleString() : ''}</span>
            }
        },
        {
            title: "Modified by",
            dataIndex: "modifiedBy",
        },
        {
            title: "Action",
            dataIndex: "action",
            width: 80,
            render: (text, record, index) => {
                return (
                    <>
                        <Tooltip title="Update">
                            <span role="button"
                                onClick={() => triggerUpdate(record.id)}
                            >
                            <Icon name="edit-2" color="#0d6efd" size={16}/>
                            </span>
                        </Tooltip>
                        {" "}
                        {/* <Tooltip title="Delete">
                            <Popconfirm
                                title={`Delete story`}
                                description={`Are you sure to delete this story?`}
                                onConfirm={() => triggerDeleteStory(record)}
                                okText="OK"
                                cancelText="Cancel"
                            >
                                <span role="button">
                                    <Icon name="trash-2" color="#dc3545" size={16}/>
                                </span>
                            </Popconfirm>
                        </Tooltip> */}
                    </>
                );
            },
        },
    ];

    const configColumns: ColumnsType<CollectionConfig> = [
        {
            title: "No.",
            render: (text, record, index) => {
                return (
                    <span>{index + 1}</span>
                )
            },
        },
        {
            title: "Name",
            dataIndex: "name",
            render: (text, record, index) => {
                return (
                    <span>{record?.name || ''}</span>
                )
            },
        },
        {
            title: "Day Min",
            dataIndex: "name",
            render: (text, record, index) => {
                return (
                    <span>{record?.config ? record.config?.dayIndexMin : ''}</span>
                )
            },
        },
        {
            title: "Day Max",
            dataIndex: "name",
            render: (text, record, index) => {
                return (
                    <span>{record?.config ? record.config?.dayIndexMax : ''}</span>
                )
            },
        },
        {
            title: "Published?",
            dataIndex: "activated",
            render: (text, record, index) => {
                return (
                    <Popconfirm
                        title={`${record.status === "active" ? "Inactive" : "Active"} Config`}
                        description={`Are you sure to ${record.status === "active" ? "Inactive" : "Active"} this Config?`}
                        onConfirm={() => updateCollectionConfigStatus(record, record.status)}
                        okText="OK"
                        cancelText="Cancel"
                    >
                        <Switch checked={record.status === 'active'} />
                    </Popconfirm>
                )
            },
        },
        {
            title: "Action",
            dataIndex: "action",
            width: 80,
            render: (text, record, index) => {
                return (
                    <>
                        <Tooltip title="Update">
                            <span role="button"
                                onClick={() => triggerUpdateConfig(record)}
                            >
                            <Icon name="edit-2" color="#0d6efd" size={16}/>
                            </span>
                        </Tooltip>
                        {" "}
                        <Tooltip title="Delete">
                            <Popconfirm
                                title={`Delete Config`}
                                description={`Are you sure to delete this config?`}
                                onConfirm={() => triggerDeleteConfig(record)}
                                okText="OK"
                                cancelText="Cancel"
                            >
                                <span role="button">
                                    <Icon name="trash-2" color="#dc3545" size={16}/>
                                </span>
                            </Popconfirm>
                        </Tooltip>
                    </>
                );
            },
        },
    ]
    
    return (
        <>
            {contextHolder}
            <CollectionConfigDrawer
                drawerVisibility={configDrawerVisibility}
                setdDrawerVisibility={setConfigDrawerVisibility}
                collectionConfig={currentConfig}
                actionType={configActionType}
                setActionType={setConfigActionType}
                onSuccess={() => {
                    api.success({
                        message: `Successfull notification`,
                        description: (actionType === ActionType.CREATE ? "Create" : "Update") + ' Config successfully!',
                        placement: 'topRight'
                    });
                    setCurrentConfig(null);
                    setConfigActionType("");
                    loadCollectionConfigs();
                }}
                onFailure={(errorMessage: string) => {
                    showError(errorMessage);
                }}
            />
            <StoryCollectionDrawer
                drawerVisibility={storyDrawerVisibility}
                setdDrawerVisibility={setStoryDrawerVisibility}
                storyCollectionDetail={storyCollectionDetail}
                setStoryCollectionDetail={setStoryCollectionDetail}
                allStoryCollections={storyCollections}
                actionType={actionType}
                setActionType={setActionType}
                onSuccess={() => {
                    api.success({
                        message: `Successfull notification`,
                        description: (actionType === ActionType.CREATE ? "Create" : "Update") + ' collection successfully!',
                        placement: 'topRight'
                    });
                    setStoryCollectionDetail(null);
                    setActionType("");
                    loadStoryCollections();
                }}
                onFailure={(errorMessage: string) => {
                    showError(errorMessage);
                }}
            />
            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Row>
                            <Col sm={6} className="mb-2">
                                <span className="fw-bold lead text-success">CONFIG</span>
                            </Col>
                            <Col sm={6} className="mb-2">
                                <div className="text-sm-end">
                                    <Button variant="success" className="mb-1 py-1 me-1" onClick={() => {
                                        // search();
                                    }}>
                                        <i className="mdi mdi-refresh me-1"></i> Refresh
                                    </Button>
                                    <Button variant="primary" className="mb-1 py-1 me-1" onClick={async () => {
                                        setConfigDrawerVisibility(true)
                                    }}>
                                        <i className="mdi mdi-plus-circle me-1"></i> Add
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                            <Table
                                loading={false}
                                rowKey={"noNumber"}
                                className="table table-hover m-0 table-centered dt-responsive nowrap w-100"
                                dataSource={collectionConfigs}
                                columns={configColumns}
                                pagination={false}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Row>
                            <Col sm={6} className="mb-2">
                                <span className="fw-bold lead text-success">COLLECTIONS</span>
                            </Col>
                            <Col sm={6} className="mb-2">
                                <div className="text-sm-end">
                                    <Button variant="success" className="mb-1 py-1 me-1" onClick={() => {
                                        // search();
                                    }}>
                                        <i className="mdi mdi-refresh me-1"></i> Refresh
                                    </Button>
                                    <Button variant="primary" className="mb-1 py-1 me-1" onClick={async () => {
                                        setStoryDrawerVisibility(true)
                                        setActionType(ActionType.CREATE);
                                    }}>
                                        <i className="mdi mdi-plus-circle me-1"></i> Add
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                            <Table
                                loading={false}
                                rowKey={"noNumber"}
                                className="table table-hover m-0 table-centered dt-responsive nowrap w-100"
                                dataSource={storyCollections}
                                columns={columns}
                                pagination={false}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default StoryCollection;
